import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Check, X as XIcon, Mail, KeyRound, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

// ---- Shared shell used by every auth screen so they all feel like the login page.
function AuthShell({ eyebrow, title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen flex bg-hearth-cream">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600210492493-0946911123ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxjb3p5JTIwbW9kZXJuJTIwbGl2aW5nJTIwcm9vbSUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4MjcyODgxMnww&ixlib=rb-4.1.0&q=85"
          alt="warm cozy home interior"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-hearth-ink/40 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h1 className="font-display text-5xl xl:text-6xl font-semibold leading-none tracking-tight">
            Your home,<br />quietly organized.
          </h1>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="inline-block mb-8"><Logo size={40} showWord /></Link>
          <p className="caption text-hearth-mute mb-3">{eyebrow}</p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-hearth-ink">{title}</h2>
          {subtitle && <p className="text-hearth-mute mt-2 leading-relaxed">{subtitle}</p>}
          <div className="mt-10">{children}</div>
          {footer && <div className="mt-8 text-sm text-hearth-mute">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

const PrimaryBtn = ({ children, loading, ...props }) => (
  <Button
    type="submit"
    disabled={loading}
    className="w-full h-12 rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white font-medium transition-transform hover:-translate-y-0.5 active:scale-[0.98]"
    {...props}
  >
    {loading ? <Loader2 className="animate-spin" size={18} /> : children}
  </Button>
);

// -------------------------------------------------------------------
// Signup
// -------------------------------------------------------------------
export function Signup() {
  const { signup, formatApiError: fmt } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setLoading(true);
    try {
      await signup(form.name.trim(), form.email.trim(), form.password);
      toast.success("Welcome home. Check your inbox to confirm your email.");
      nav("/", { replace: true });
    } catch (e2) {
      toast.error(fmt(e2.response?.data?.detail) || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      eyebrow="Create account"
      title="A place for your home."
      subtitle="A few details and you're in. You can invite the rest of the household from inside."
      footer={<>Already have an account?{" "}
        <Link to="/login" data-testid="signup-login-link" className="text-hearth-terracotta font-medium underline-offset-4 hover:underline">Sign in</Link>
      </>}
    >
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="caption text-hearth-mute">Your name</Label>
          <Input data-testid="signup-name-input" required value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="input-warm mt-2 h-12" placeholder="What should we call you?" />
        </div>
        <div>
          <Label className="caption text-hearth-mute">Email</Label>
          <Input data-testid="signup-email-input" type="email" required value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="input-warm mt-2 h-12" placeholder="you@home.com" />
        </div>
        <div>
          <Label className="caption text-hearth-mute">Password</Label>
          <Input data-testid="signup-password-input" type="password" required value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })}
                 className="input-warm mt-2 h-12" placeholder="At least 8 characters" />
        </div>
        <PrimaryBtn data-testid="signup-submit-button" loading={loading}>Create account</PrimaryBtn>
      </form>
    </AuthShell>
  );
}

// -------------------------------------------------------------------
// Forgot password
// -------------------------------------------------------------------
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      eyebrow="Password reset"
      title={sent ? "Check your inbox." : "Forgot your password?"}
      subtitle={sent
        ? "If that email is on file, a reset link is on its way. It'll expire in an hour, so pop back here shortly."
        : "Enter the email you used to sign up. We'll send you a link to choose a new password."}
      footer={<Link to="/login" data-testid="forgot-back-link" className="inline-flex items-center gap-1 text-hearth-clayDark hover:text-hearth-clay">
        <ArrowLeft size={13} /> Back to sign in
      </Link>}
    >
      {!sent ? (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="caption text-hearth-mute">Email</Label>
            <Input data-testid="forgot-email-input" type="email" required value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   className="input-warm mt-2 h-12" placeholder="you@home.com" />
          </div>
          <PrimaryBtn data-testid="forgot-submit-button" loading={loading}>
            <Mail size={14} className="mr-1.5" /> Send reset link
          </PrimaryBtn>
        </form>
      ) : (
        <div className="warm-card p-6 border-hearth-sage/40 bg-hearth-sage/5 text-hearth-sageDark inline-flex items-start gap-3">
          <Check size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Email sent to {email}</p>
            <p className="text-xs text-hearth-mute mt-1">Didn't arrive? Check spam, then <button className="underline" onClick={() => setSent(false)}>try again</button>.</p>
          </div>
        </div>
      )}
    </AuthShell>
  );
}

// -------------------------------------------------------------------
// Reset password (accepts ?token=... in URL)
// -------------------------------------------------------------------
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Couldn't reset password");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      eyebrow="New password"
      title={done ? "All set." : "Choose a new password."}
      subtitle={done
        ? "Redirecting you to sign in…"
        : "It should be at least 8 characters. Pick something you'll remember but nobody else could guess."}
    >
      {!token ? (
        <p className="text-hearth-coral text-sm">This reset link is missing or malformed — please request a new one from the sign-in page.</p>
      ) : done ? (
        <div className="warm-card p-6 border-hearth-sage/40 bg-hearth-sage/5 text-hearth-sageDark inline-flex items-center gap-3">
          <Check size={18} /><span>Your password has been changed.</span>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="caption text-hearth-mute">New password</Label>
            <Input data-testid="reset-password-input" type="password" required value={pw}
                   onChange={(e) => setPw(e.target.value)}
                   className="input-warm mt-2 h-12" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label className="caption text-hearth-mute">Confirm password</Label>
            <Input data-testid="reset-password-confirm" type="password" required value={pw2}
                   onChange={(e) => setPw2(e.target.value)}
                   className="input-warm mt-2 h-12" placeholder="Type it again" />
          </div>
          <PrimaryBtn data-testid="reset-submit-button" loading={loading}>
            <KeyRound size={14} className="mr-1.5" /> Change password
          </PrimaryBtn>
        </form>
      )}
    </AuthShell>
  );
}

// -------------------------------------------------------------------
// Verify email (accepts ?token=... in URL)
// -------------------------------------------------------------------
export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("pending"); // pending | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setMessage("Missing confirmation token."); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("ok");
      } catch (e) {
        setState("error");
        setMessage(formatApiError(e.response?.data?.detail) || "Verification failed.");
      }
    })();
  }, [token]);

  return (
    <AuthShell
      eyebrow="Confirm email"
      title={
        state === "pending" ? "Confirming your email…" :
        state === "ok" ? "You're confirmed." :
        "That link didn't work."
      }
      subtitle={
        state === "pending" ? "One moment." :
        state === "ok" ? "Welcome to HearthHouse. You can now receive password resets and reminders from us." :
        message
      }
      footer={
        <Link to={state === "ok" ? "/" : "/login"} data-testid="verify-continue-link"
              className="text-hearth-terracotta font-medium underline-offset-4 hover:underline">
          {state === "ok" ? "Continue to your home" : "Back to sign in"}
        </Link>
      }
    >
      <div className="warm-card p-6 inline-flex items-center gap-3">
        {state === "pending" && <Loader2 size={20} className="animate-spin text-hearth-clay" />}
        {state === "ok" && <Check size={20} className="text-hearth-sageDark" />}
        {state === "error" && <XIcon size={20} className="text-hearth-coral" />}
        <span className="text-sm text-hearth-mute">
          {state === "pending" ? "Verifying…" : state === "ok" ? "All done." : "Please request a fresh link."}
        </span>
      </div>
    </AuthShell>
  );
}
